import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ChevronRight,
  Mail,
  Phone,
  Settings2,
  ShieldCheck,
  Store,
  UserRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";

function DetailRow({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  tone: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3">
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}
      >
        <Icon className="h-4.5 w-4.5" />
      </span>
      <span className="min-w-0">
        <span className="block text-xs font-medium text-slate-500">
          {label}
        </span>
        <span className="mt-0.5 block truncate text-sm font-semibold text-slate-900">
          {value}
        </span>
      </span>
    </div>
  );
}

export function ProfilePage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const selectedStoreId = useAuthStore((state) => state.selectedStoreId);

  if (!user) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="text-sm text-slate-500">Loading profile...</p>
      </div>
    );
  }

  const primaryStore = user.stores?.[0];
  const activeStore =
    user.stores?.find((store) => store.id === selectedStoreId) || primaryStore;
  const emailName = user.email
    .split("@")[0]
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
  const fullName =
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    emailName ||
    "Account user";
  const initials =
    `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}` || "U";
  const roleLabel = (user.roleName || user.role || "Staff")
    .replace(/_/g, " ")
    .toLowerCase();
  const storeCount = user.stores?.length || 0;

  return (
    <div className="mx-auto max-w-6xl animate-fade-in space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/settings")}
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to settings
          </button>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            Profile
          </h1>
          <p className="mt-1 text-sm text-slate-500 sm:text-base">
            Your identity, contact details and account access at a glance.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="inline-flex h-10 w-fit items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-teal-200 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
        >
          <Settings2 className="h-4 w-4" />
          Account settings
        </button>
      </header>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="min-w-0 space-y-6" aria-label="Profile details">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="relative overflow-hidden bg-slate-950 px-5 py-6 sm:px-7">
              <div className="absolute -right-16 -top-24 h-56 w-56 rounded-full bg-teal-500/20 blur-2xl" />
              <div className="absolute -bottom-24 right-1/3 h-44 w-44 rounded-full bg-cyan-400/10 blur-2xl" />
              <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-gradient-to-br from-teal-400 to-teal-600 text-xl font-bold text-white shadow-lg shadow-teal-950/20">
                  {initials.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate text-xl font-bold text-white sm:text-2xl">
                      {fullName}
                    </h2>
                    <BadgeCheck className="h-5 w-5 shrink-0 text-teal-300" />
                  </div>
                  <p className="mt-1 truncate text-sm text-slate-300">
                    {user.email}
                  </p>
                </div>
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-emerald-300/20 bg-emerald-400/10 px-3 py-1.5 text-xs font-semibold capitalize text-emerald-200">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {roleLabel}
                </span>
              </div>
            </div>

            <div className="px-5 py-5 sm:px-7 sm:py-6">
              <div className="mb-4">
                <h3 className="font-semibold text-slate-900">
                  Personal information
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Contact details associated with your sign-in account.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <DetailRow
                  icon={Mail}
                  label="Email address"
                  value={user.email}
                  tone="bg-sky-50 text-sky-600"
                />
                <DetailRow
                  icon={Phone}
                  label="Phone number"
                  value={user.phone || "Not provided"}
                  tone="bg-violet-50 text-violet-600"
                />
                <DetailRow
                  icon={UserRound}
                  label="Display name"
                  value={fullName}
                  tone="bg-teal-50 text-teal-700"
                />
                <DetailRow
                  icon={Building2}
                  label="Primary store"
                  value={primaryStore?.name || "No store assigned"}
                  tone="bg-amber-50 text-amber-700"
                />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">
                  Profile details are protected
                </h3>
                <p className="mt-1 text-sm leading-5 text-slate-500">
                  Contact your business administrator when your name, phone or
                  assigned store needs to change. This prevents accidental
                  updates to staff access.
                </p>
              </div>
            </div>
          </section>
        </section>

        <aside className="space-y-4 lg:sticky lg:top-24">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Account access
                </p>
                <h3 className="mt-1 font-semibold text-slate-900">
                  Active workspace
                </h3>
              </div>
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                <Store className="h-4.5 w-4.5" />
              </span>
            </div>

            <dl className="mt-5 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-slate-500">Current store</dt>
                <dd className="max-w-[170px] text-right text-sm font-semibold text-slate-900">
                  {activeStore?.name || "Not selected"}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-slate-500">Role</dt>
                <dd className="text-right text-sm font-semibold capitalize text-slate-900">
                  {roleLabel}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-slate-500">Store access</dt>
                <dd className="text-right text-sm font-semibold text-slate-900">
                  {storeCount} {storeCount === 1 ? "store" : "stores"}
                </dd>
              </div>
            </dl>

            <div className="mt-5 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2.5 text-xs font-medium text-emerald-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Account is active
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
            <p className="px-3 pb-2 pt-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Assigned stores
            </p>
            {user.stores?.length ? (
              <div className="space-y-1">
                {user.stores.map((store) => {
                  const isActive = store.id === activeStore?.id;
                  return (
                    <div
                      key={store.id}
                      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${
                        isActive ? "bg-teal-50" : "bg-white"
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                          isActive
                            ? "bg-white text-teal-700 shadow-sm"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        <Store className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                        {store.name}
                      </span>
                      {isActive && (
                        <span className="text-[11px] font-semibold text-teal-700">
                          Active
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="px-3 pb-3 text-sm text-slate-500">
                No stores are assigned to this account.
              </p>
            )}
          </section>

          <button
            type="button"
            onClick={() => navigate("/settings")}
            className="group flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition hover:border-teal-200 hover:bg-teal-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          >
            <span>
              <span className="block text-sm font-semibold text-slate-800">
                Application settings
              </span>
              <span className="mt-0.5 block text-xs text-slate-500">
                Alerts, printers and invoices
              </span>
            </span>
            <ChevronRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-teal-600" />
          </button>
        </aside>
      </div>
    </div>
  );
}
